import React, { useEffect, useState } from 'react'
import { Eye, EyeOff, Flag, MessageCircle, Pencil, Trash2 } from 'lucide-react'
import { brandConfirm } from '../../contexts/DialogContext'
import {
  adminDeleteCommunityPost,
  COMMUNITY_REACTION_EMOJI,
  CommunityCommentRow,
  CommunityPostRow,
  CommunityReactionRow,
  createCommunityComment,
  createCommunityReport,
  deleteOwnCommunityComment,
  editCommunityComment,
  editCommunityPost,
  fetchCommentsForPost,
  fetchReactionsFor,
  removeOwnCommunityPost,
  setCommunityCommentHidden,
  setCommunityPostHidden,
  toggleCommunityReaction,
} from '../../lib/communityData'
import CommunityMedia from '../ui/CommunityMedia'
import GradientButton from '../ui/GradientButton'

interface CommunityPostCardProps {
  post: CommunityPostRow
  userId: string
  token: string
  isAdmin: boolean
  onChanged: () => void
}

const formatTimestamp = (createdAt: number) => {
  const date = new Date(createdAt)
  const now = new Date()
  const sameDay = date.toDateString() === now.toDateString()
  if (sameDay) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

const CommunityPostCard: React.FC<CommunityPostCardProps> = ({ post, userId, token, isAdmin, onChanged }) => {
  const isOwner = post.authorId === userId
  const withinEditWindow = !!post.editWindowExpiresAt && Date.now() < post.editWindowExpiresAt
  const canEdit = isAdmin || (isOwner && withinEditWindow)
  // Admin moderation (hide/unhide, permanent delete) is a distinct action
  // from a member removing their own post — kept separate so an admin
  // acting on someone else's post is never recorded as if the author
  // removed it themselves (see handleAdminHide/handleAdminDelete below).
  const canSelfRemove = isOwner && !isAdmin

  const [reactions, setReactions] = useState<CommunityReactionRow[]>([])
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [comments, setComments] = useState<CommunityCommentRow[]>([])
  const [loadingComments, setLoadingComments] = useState(false)
  const [commentDraft, setCommentDraft] = useState('')
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyDraft, setReplyDraft] = useState('')
  const [editingPost, setEditingPost] = useState(false)
  const [editDraft, setEditDraft] = useState(post.content)
  const [reportOpen, setReportOpen] = useState(false)
  const [reportReason, setReportReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchReactionsFor('post', [post.id]).then(setReactions).catch(() => {})
  }, [post.id])

  const loadComments = async () => {
    setLoadingComments(true)
    try {
      setComments(await fetchCommentsForPost(post.id))
    } catch (err) {
      console.error('Failed to load comments:', err)
    } finally {
      setLoadingComments(false)
    }
  }

  const handleToggleComments = async () => {
    const next = !commentsOpen
    setCommentsOpen(next)
    if (next && comments.length === 0) await loadComments()
  }

  const handleReact = async (emoji: string) => {
    try {
      await toggleCommunityReaction(userId, 'post', post.id, emoji)
      setReactions(await fetchReactionsFor('post', [post.id]))
    } catch (err) {
      console.error('Failed to react:', err)
    }
  }

  const handleSaveEdit = async () => {
    setBusy(true)
    setError('')
    try {
      await editCommunityPost(token, { postId: post.id, content: editDraft.trim() })
      setEditingPost(false)
      onChanged()
    } catch (err: any) {
      setError(err?.message || 'Could not save your changes.')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async () => {
    setBusy(true)
    try {
      await removeOwnCommunityPost(post.id)
      onChanged()
    } catch (err) {
      console.error('Failed to remove post:', err)
    } finally {
      setBusy(false)
    }
  }

  /** Admin-only: hide/unhide from members without erasing anything — a reversible moderation flag, distinct from permanent delete below. */
  const handleAdminHide = async () => {
    setBusy(true)
    try {
      await setCommunityPostHidden(post.id, !post.hiddenByAdmin)
      onChanged()
    } catch (err) {
      console.error('Failed to toggle post visibility:', err)
    } finally {
      setBusy(false)
    }
  }

  /** Admin-only: true, irreversible delete of the post plus its comments/reactions/reports — always confirmed first. */
  const handleAdminDelete = async () => {
    const ok = await brandConfirm({
      title: 'Permanently delete this post?',
      message: `This removes "${post.authorDisplayName}"'s post and its comments for good — not just hide it. This can't be undone.`,
      variant: 'danger',
      confirmLabel: 'Delete permanently',
      cancelLabel: 'Cancel',
    })
    if (!ok) return
    setBusy(true)
    try {
      await adminDeleteCommunityPost(post.id)
      onChanged()
    } catch (err) {
      console.error('Failed to permanently delete post:', err)
    } finally {
      setBusy(false)
    }
  }

  const handleSubmitComment = async (parentCommentId?: string) => {
    const content = (parentCommentId ? replyDraft : commentDraft).trim()
    if (!content) return
    setBusy(true)
    setError('')
    try {
      await createCommunityComment(token, { postId: post.id, content, parentCommentId })
      if (parentCommentId) {
        setReplyDraft('')
        setReplyTo(null)
      } else {
        setCommentDraft('')
      }
      await loadComments()
    } catch (err: any) {
      setError(err?.message || 'Could not post your comment.')
    } finally {
      setBusy(false)
    }
  }

  const handleSubmitReport = async () => {
    setBusy(true)
    try {
      await createCommunityReport(userId, 'post', post.id, reportReason.trim())
      setReportOpen(false)
      setReportReason('')
    } catch (err) {
      console.error('Failed to submit report:', err)
    } finally {
      setBusy(false)
    }
  }

  const topLevelComments = comments.filter((c) => !c.parentCommentId)
  const repliesFor = (commentId: string) => comments.filter((c) => c.parentCommentId === commentId)

  const reactionCounts = COMMUNITY_REACTION_EMOJI.map((emoji) => ({
    emoji,
    count: reactions.filter((r) => r.emoji === emoji).length,
    mine: reactions.some((r) => r.emoji === emoji && r.userId === userId),
  }))

  return (
    <div className="panel-card panel-card-body">
      {(post.hiddenByAdmin || post.removedByAuthor) && isAdmin && (
        <div className="mb-3 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-lg px-3 py-1.5 inline-block">
          {post.removedByAuthor ? 'Removed by author' : 'Hidden by admin'} — visible to you only (moderation record)
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-ink">{post.authorDisplayName}</p>
          <p className="text-xs text-gray-400">{formatTimestamp(post.createdAt)}</p>
        </div>
        <div className="flex items-center gap-2 text-gray-400">
          {canEdit && !editingPost && (
            <button onClick={() => setEditingPost(true)} className="hover:text-ink" title="Edit">
              <Pencil size={16} />
            </button>
          )}
          {isAdmin ? (
            <>
              <button
                onClick={handleAdminHide}
                disabled={busy}
                className="flex items-center gap-1 text-xs font-medium hover:text-ink"
                title={post.hiddenByAdmin ? 'Unhide — make visible to members again' : 'Hide from members (keeps the post as a moderation record)'}
              >
                {post.hiddenByAdmin ? <Eye size={16} /> : <EyeOff size={16} />}
                {post.hiddenByAdmin ? 'Unhide' : 'Hide'}
              </button>
              <button
                onClick={handleAdminDelete}
                disabled={busy}
                className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-600"
                title="Delete permanently"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </>
          ) : (
            canSelfRemove && (
              <button onClick={handleRemove} disabled={busy} className="hover:text-red-600" title="Remove">
                <Trash2 size={16} />
              </button>
            )
          )}
          {!isOwner && (
            <button onClick={() => setReportOpen((v) => !v)} className="hover:text-red-600" title="Report">
              <Flag size={16} />
            </button>
          )}
        </div>
      </div>

      {editingPost ? (
        <div className="mt-3">
          <textarea
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            rows={3}
            className="form-input w-full resize-none"
          />
          <div className="flex gap-2 mt-2">
            <GradientButton onClick={handleSaveEdit} size="sm" disabled={busy}>Save</GradientButton>
            <GradientButton onClick={() => setEditingPost(false)} size="sm" variant="outline">Cancel</GradientButton>
          </div>
        </div>
      ) : (
        post.content && <p className="mt-3 whitespace-pre-wrap break-words text-textPrimary">{post.content}</p>
      )}

      {post.mediaPath && (
        <div className="mt-3 rounded-xl overflow-hidden max-h-96">
          <CommunityMedia postId={post.id} mediaType={post.mediaType} className="w-full max-h-96 object-contain bg-black/5" />
        </div>
      )}

      {reportOpen && (
        <div className="mt-3 p-3 bg-cream rounded-xl">
          <textarea
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Why are you reporting this? (optional)"
            rows={2}
            className="form-input w-full resize-none"
          />
          <div className="flex gap-2 mt-2">
            <GradientButton onClick={handleSubmitReport} size="sm" disabled={busy}>Submit report</GradientButton>
            <GradientButton onClick={() => setReportOpen(false)} size="sm" variant="outline">Cancel</GradientButton>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
        {reactionCounts.map(({ emoji, count, mine }) => (
          <button
            key={emoji}
            onClick={() => handleReact(emoji)}
            className={`text-sm px-2 py-1 rounded-full border transition-colors ${
              mine ? 'border-brand-blush bg-brand-blush/15 text-brand-navy' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {emoji} {count > 0 && count}
          </button>
        ))}
        {!post.commentsDisabled && (
          <button
            onClick={handleToggleComments}
            className="ml-auto flex items-center gap-1.5 text-sm text-gray-500 hover:text-ink"
          >
            <MessageCircle size={16} />
            {comments.length > 0 ? comments.length : ''} {commentsOpen ? 'Hide comments' : 'Comments'}
          </button>
        )}
      </div>

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      {commentsOpen && !post.commentsDisabled && (
        <div className="mt-4 space-y-3">
          {loadingComments ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gold" />
            </div>
          ) : (
            topLevelComments.map((comment) => (
              <div key={comment.id} className="pl-3 border-l-2 border-gray-100">
                <CommentRow
                  comment={comment}
                  userId={userId}
                  token={token}
                  isAdmin={isAdmin}
                  onChanged={loadComments}
                  onReply={() => setReplyTo(comment.id)}
                />
                {repliesFor(comment.id).map((reply) => (
                  <div key={reply.id} className="pl-4 mt-2 border-l-2 border-gray-50">
                    <CommentRow
                      comment={reply}
                      userId={userId}
                      token={token}
                      isAdmin={isAdmin}
                      onChanged={loadComments}
                    />
                  </div>
                ))}
                {replyTo === comment.id && (
                  <div className="mt-2 ml-4 flex gap-2">
                    <input
                      value={replyDraft}
                      onChange={(e) => setReplyDraft(e.target.value)}
                      placeholder="Write a reply..."
                      className="form-input flex-1 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSubmitComment(comment.id)
                      }}
                    />
                    <GradientButton onClick={() => handleSubmitComment(comment.id)} size="sm" disabled={busy || !replyDraft.trim()}>
                      Reply
                    </GradientButton>
                  </div>
                )}
              </div>
            ))
          )}

          <div className="flex gap-2 pt-2">
            <input
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              placeholder="Write a comment..."
              className="form-input flex-1 text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmitComment()
              }}
            />
            <GradientButton onClick={() => handleSubmitComment()} size="sm" disabled={busy || !commentDraft.trim()}>
              Post
            </GradientButton>
          </div>
        </div>
      )}
    </div>
  )
}

const CommentRow: React.FC<{
  comment: CommunityCommentRow
  userId: string
  token: string
  isAdmin: boolean
  onChanged: () => void
  onReply?: () => void
}> = ({ comment, userId, token, isAdmin, onChanged, onReply }) => {
  const isOwner = comment.authorId === userId
  const withinEditWindow = !!comment.editWindowExpiresAt && Date.now() < comment.editWindowExpiresAt
  const canEdit = isAdmin || (isOwner && withinEditWindow)
  const canDelete = isAdmin || isOwner

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(comment.content)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await editCommunityComment(token, { commentId: comment.id, content: draft.trim() })
      setEditing(false)
      onChanged()
    } catch (err) {
      console.error('Failed to edit comment:', err)
    } finally {
      setBusy(false)
    }
  }

  /** Admin-only: hide/unhide a comment without erasing it — mirrors the post-level Hide action. */
  const handleToggleHidden = async () => {
    setBusy(true)
    try {
      await setCommunityCommentHidden(comment.id, !comment.hiddenByAdmin)
      onChanged()
    } catch (err) {
      console.error('Failed to toggle comment visibility:', err)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    // An admin removing someone else's comment is a permanent, irreversible
    // moderation action, so it confirms first. A member deleting their own
    // comment keeps its existing one-click behavior.
    if (isAdmin && !isOwner) {
      const ok = await brandConfirm({
        title: 'Permanently delete this comment?',
        message: 'This removes the comment for good. This can\'t be undone.',
        variant: 'danger',
        confirmLabel: 'Delete permanently',
        cancelLabel: 'Cancel',
      })
      if (!ok) return
    }
    setBusy(true)
    try {
      await deleteOwnCommunityComment(comment.id)
      onChanged()
    } catch (err) {
      console.error('Failed to delete comment:', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-ink">
          {comment.authorDisplayName}
          {isAdmin && comment.hiddenByAdmin && (
            <span className="ml-2 text-[10px] font-medium text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 align-middle">
              Hidden
            </span>
          )}
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>{formatTimestamp(comment.createdAt)}</span>
          {canEdit && !editing && <button onClick={() => setEditing(true)} className="hover:text-ink">Edit</button>}
          {isAdmin && (
            <button onClick={handleToggleHidden} disabled={busy} className="hover:text-ink">
              {comment.hiddenByAdmin ? 'Unhide' : 'Hide'}
            </button>
          )}
          {canDelete && <button onClick={remove} disabled={busy} className="hover:text-red-600">Delete</button>}
          {onReply && <button onClick={onReply} className="hover:text-ink">Reply</button>}
        </div>
      </div>
      {editing ? (
        <div className="mt-1 flex gap-2">
          <input value={draft} onChange={(e) => setDraft(e.target.value)} className="form-input flex-1 text-sm" />
          <GradientButton onClick={save} size="sm" disabled={busy}>Save</GradientButton>
        </div>
      ) : (
        <p className="text-sm text-textPrimary whitespace-pre-wrap break-words">{comment.content}</p>
      )}
    </div>
  )
}

export default CommunityPostCard
